/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Tags API Client
 *
 * Handles CRUD operations for tags - both standard (global) and custom (project-specific).
 * Tags are used for clip categorization with color coding and usage statistics.
 *
 * Standard tags (VFX, Dailies, Stock, Graphics, Audio) are read-only.
 * Custom tags can be created, updated, and deleted per project.
 *
 * Automatically injects Supabase JWT tokens for authentication.
 */

import { supabase } from '@/lib/supabase'
import type { Tag, TagCreateInput, TagUpdateInput, TagStatistics } from '@/types/database'

// =============================================
// API CLIENT CONFIGURATION
// =============================================

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get the current Supabase JWT token for authentication
 */
async function getAuthToken(): Promise<string> {
  const { data: { session }, error } = await supabase.auth.getSession()

  if (error) {
    console.error('Auth error:', error)
    throw new Error('Authentication error. Please try logging in again.')
  }

  if (!session) {
    throw new Error('Not authenticated. Please log in.')
  }

  return session.access_token
}

/**
 * Make an authenticated request to the backend API
 */
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken()

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  return response
}

// =============================================
// TAG ENDPOINTS
// =============================================

/**
 * List all tags for a project (standard + custom)
 */
export async function listProjectTags(
  projectId: string,
  includeStandard = true,
  sortBy: 'name' | 'usage_count' | 'created_at' = 'name'
): Promise<Tag[]> {
  const url = `${BACKEND_URL}/api/projects/${projectId}/tags?include_standard=${includeStandard}&sort_by=${sortBy}`
  const response = await fetchWithAuth(url)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch tags' }))
    throw new Error(error.detail || 'Failed to fetch tags')
  }

  return response.json()
}

/**
 * List all standard tags (VFX, Dailies, Stock, Graphics, Audio)
 */
export async function getStandardTags(): Promise<Tag[]> {
  const url = `${BACKEND_URL}/api/tags/standard`
  const response = await fetchWithAuth(url)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch standard tags' }))
    throw new Error(error.detail || 'Failed to fetch standard tags')
  }

  return response.json()
}

/**
 * Create a new custom tag for a project
 */
export async function createTag(projectId: string, data: TagCreateInput): Promise<Tag> {
  const url = `${BACKEND_URL}/api/projects/${projectId}/tags`
  const response = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create tag' }))
    throw new Error(error.detail || 'Failed to create tag')
  }

  return response.json()
}

/**
 * Update a custom tag
 */
export async function updateTag(tagId: string, data: TagUpdateInput): Promise<Tag> {
  const url = `${BACKEND_URL}/api/tags/${tagId}`
  const response = await fetchWithAuth(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update tag' }))
    throw new Error(error.detail || 'Failed to update tag')
  }

  return response.json()
}

/**
 * Delete a custom tag
 */
export async function deleteTag(tagId: string): Promise<void> {
  const url = `${BACKEND_URL}/api/tags/${tagId}`
  const response = await fetchWithAuth(url, {
    method: 'DELETE',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete tag' }))
    throw new Error(error.detail || 'Failed to delete tag')
  }
}

/**
 * Get tag usage statistics for a project
 */
export async function getTagStatistics(projectId: string): Promise<TagStatistics[]> {
  const url = `${BACKEND_URL}/api/projects/${projectId}/tags/statistics`
  const response = await fetchWithAuth(url)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch tag statistics' }))
    throw new Error(error.detail || 'Failed to fetch tag statistics')
  }

  return response.json()
}

// =============================================
// CLIP TAG ENDPOINTS
// =============================================

/**
 * Add tags to a clip
 */
export async function addTagsToClip(clipId: string, tagNames: string[]): Promise<void> {
  const url = `${BACKEND_URL}/api/clips/${clipId}/tags`
  const response = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify({ tag_names: tagNames }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to add tags to clip' }))
    throw new Error(error.detail || 'Failed to add tags to clip')
  }
}

/**
 * Remove tags from a clip
 */
export async function removeTagsFromClip(clipId: string, tagNames: string[]): Promise<void> {
  const url = `${BACKEND_URL}/api/clips/${clipId}/tags`
  const response = await fetchWithAuth(url, {
    method: 'DELETE',
    body: JSON.stringify({ tag_names: tagNames }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to remove tags from clip' }))
    throw new Error(error.detail || 'Failed to remove tags from clip')
  }
}
